import { useEffect, useRef } from "react";
import Icon from "../Icon";
import { Spinner } from "./estado";

/* -------------------------------- MODAL ------------------------------- */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide = false,
}) {
  const ref = useRef(null);

  /*
   * El foco entra al diálogo UNA vez, al abrirse.
   *
   * Antes esto vivía junto al listener de Escape, en un efecto que dependía
   * de `onClose`. Como los llamadores pasan una función nueva en cada render
   * (`onClose={() => setX(null)}`), el efecto se reejecutaba con cada
   * pulsación y devolvía el foco al contenedor: se escribía una letra y el
   * campo quedaba muerto. Pasaba en TODOS los formularios del panel.
   */
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  /*
   * `onClose` se lee por referencia para que cambiar su identidad no vuelva a
   * montar el listener ni bloquee el scroll de nuevo.
   *
   * La asignación va en un efecto y no suelta en el cuerpo: escribir una `ref`
   * durante el render es una mutación durante el render, y con el modo
   * concurrente React puede descartar ese render y quedarse con la referencia
   * de uno que nunca se pintó.
   */
  const cerrarRef = useRef(onClose);

  useEffect(() => {
    cerrarRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    /*
     * EL FOCO SE QUEDA DENTRO del diálogo.
     *
     * Sin esto, Tab salía al menú y a la tabla que están DEBAJO del velo: quien
     * navega con teclado seguía tabulando por una pantalla que visualmente ya no
     * existía, sin forma de saber dónde estaba. Con Shift+Tab desde el primer
     * elemento pasa lo mismo hacia atrás, y por eso se cubren los dos sentidos.
     */
    const enfocables = () =>
      [
        ...(ref.current?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter((el) => el.offsetParent !== null);

    const onKey = (e) => {
      if (e.key === "Escape") {
        cerrarRef.current?.();
        return;
      }

      if (e.key !== "Tab") return;

      const lista = enfocables();
      if (lista.length === 0) return;

      const primero = lista[0];
      const ultimo = lista[lista.length - 1];

      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        className={`modal${wide ? " modal-lg" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <header className="modal-head">
          <h2 className="card-title grow">{title}</h2>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icon name="close" size={17} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}


/** Confirmación destructiva: un solo diálogo para toda la app. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Eliminar",
  busy,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy && <Spinner size={14} />}
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="muted">{message}</p>
    </Modal>
  );
}
